import React from 'react';
import { Icon } from '@iconify/react';
import { Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';

interface GeneralStatsTabProps {
  data: {
    clicks: number;
    subscribers: number;
    totalTransactions: number;
    revenue: number;
    ctr: number;
    arpu: number;
    cvpc: number;
    rpc: number;
  };
  fansData: {
    paidSubscription: { count: number; percentage: number };
    recurringSubscription: { count: number; percentage: number };
    purchases: { count: number; percentage: number };
    purchases500: { count: number; percentage: number };
    messages3: { count: number; percentage: number };
  };
}

const GeneralStatsTab: React.FC<GeneralStatsTabProps> = ({ data, fansData }) => {
  const { t } = useTranslations();

  const formatCurrency = (value: number): string => {
    if (!Number.isFinite(value)) {
      return '$0.00';
    }

    return `$${value.toFixed(2)}`;
  };

  const statsItems = [
    {
      icon: 'solar:cursor-square-linear',
      label: t('campaignsStats.modal.generalStats.clicks'),
      value: data.clicks,
    },
    {
      icon: 'solar:users-group-two-rounded-linear',
      label: t('campaignsStats.modal.generalStats.subscribers'),
      value: data.subscribers,
    },
    {
      icon: 'solar:banknote-3-linear',
      label: t('campaignsStats.modal.generalStats.totalTransactions'),
      value: data.totalTransactions,
    },
    {
      icon: 'solar:card-recive-linear',
      label: t('campaignsStats.modal.generalStats.revenue'),
      value: formatCurrency(data.revenue),
    },
    {
      icon: 'solar:sale-square-linear',
      label: t('campaignsStats.modal.generalStats.ctr'),
      value: `${Number(data.ctr).toFixed(1)}%`,
    },
  ];

  const detailedStats = [
    {
      icon: 'solar:user-check-linear',
      label: t('campaignsStats.modal.generalStats.arpu'),
      description: t('campaignsStats.modal.generalStats.arpuDescription'),
      value: `$${data.arpu.toFixed(3)}`,
    },
    {
      icon: 'solar:dollar-minimalistic-linear',
      label: t('campaignsStats.modal.generalStats.cvpc'),
      description: t('campaignsStats.modal.generalStats.cvpcDescription'),
      value: `$${data.cvpc.toFixed(3)}`,
    },
    {
      icon: 'solar:hand-money-linear',
      label: t('campaignsStats.modal.generalStats.rpc'),
      value: `$${data.rpc.toFixed(3)}`,
    },
  ];

  const fansCategories = [
    {
      label: t('campaignsStats.modal.fansCategories.paidSubscription'),
      count: fansData.paidSubscription.count,
      percentage: fansData.paidSubscription.percentage,
    },
    {
      label: t('campaignsStats.modal.fansCategories.recurringSubscription'),
      count: fansData.recurringSubscription.count,
      percentage: fansData.recurringSubscription.percentage,
    },
    {
      label: t('campaignsStats.modal.fansCategories.purchases'),
      count: fansData.purchases.count,
      percentage: fansData.purchases.percentage,
    },
    {
      label: t('campaignsStats.modal.fansCategories.purchases500'),
      count: fansData.purchases500.count,
      percentage: fansData.purchases500.percentage,
    },
    {
      label: `${fansData.messages3.count} ${t('campaignsStats.modal.fansCategories.messages')}`,
      count: fansData.messages3.count,
      percentage: fansData.messages3.percentage,
      hasInfo: true,
    },
  ];

  return (
    <div className="flex flex-col gap-0">
      {/* Stats Section */}
      <div className="border-b border-[#e2ebf7] px-4 py-4">
        <div className="flex flex-col gap-2">
          {statsItems.map((item, index) => (
            <div key={index} className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2 flex-1">
                <Icon
                  icon={item.icon}
                  width={16}
                  height={16}
                  className="text-zinc-500 flex-shrink-0"
                />
                <Typography variant="body2" className="text-zinc-500 text-xs leading-4">
                  {item.label}
                </Typography>
              </div>
              <Typography
                variant="body2"
                className="text-auth-ink text-xs leading-4 whitespace-nowrap"
              >
                {item.value}
              </Typography>
            </div>
          ))}

          {detailedStats.map((item, index) => (
            <div key={index} className="flex items-start justify-between w-full">
              <div className="flex items-start gap-2 flex-1">
                <Icon
                  icon={item.icon}
                  width={16}
                  height={16}
                  className="text-zinc-500 flex-shrink-0 mt-0"
                />
                <div className="flex flex-col">
                  <Typography variant="body2" className="text-zinc-500 text-xs leading-4">
                    {item.label}
                  </Typography>
                  {item.description && (
                    <Typography variant="body2" className="text-zinc-500 text-xs leading-4">
                      {item.description}
                    </Typography>
                  )}
                </div>
              </div>
              <Typography
                variant="body2"
                className="text-auth-ink text-xs leading-4 whitespace-nowrap"
              >
                {item.value}
              </Typography>
            </div>
          ))}
        </div>
      </div>

      {/* Fans Categories Section */}
      <div className="bg-white px-4 py-4 flex flex-col gap-2">
        {/* Header */}
        <div className="flex items-center px-0 py-1.5">
          <div className="flex-1">
            <Typography variant="body2" className="text-zinc-500 text-xs leading-4 font-medium">
              {t('campaignsStats.modal.fansCategories.title')}
            </Typography>
          </div>
          <div className="w-[9rem]">
            <Typography variant="body2" className="text-zinc-500 text-xs leading-4 font-medium">
              {t('campaignsStats.modal.fansCategories.fansCount')}
            </Typography>
          </div>
          <div className="w-[9rem]">
            <Typography variant="body2" className="text-zinc-500 text-xs leading-4 font-medium">
              {t('campaignsStats.modal.fansCategories.percentage')}
            </Typography>
          </div>
        </div>

        {/* Rows */}
        <div className="flex flex-col gap-0">
          {fansCategories.map((item, index) => (
            <div key={index} className="flex items-center px-0 py-1.5">
              <div className="flex-1 flex items-center gap-0.5">
                <Typography variant="body2" className="text-zinc-500 text-xs leading-4">
                  {item.label}
                </Typography>
                {item.hasInfo && (
                  <Icon
                    icon="solar:info-circle-bold"
                    width={16}
                    height={16}
                    className="text-zinc-500"
                  />
                )}
              </div>
              <div className="w-[9rem]">
                <Typography variant="body2" className="text-auth-ink text-xs leading-4 font-medium">
                  {item.count}
                </Typography>
              </div>
              <div className="w-[9rem]">
                <Typography variant="body2" className="text-auth-ink text-xs leading-4 font-medium">
                  {Number(item.percentage).toFixed(1)}%
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GeneralStatsTab;
